import { Button } from '@m/alchemy-ui/Button';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { Field } from '@m/alchemy-ui/Field';
import { Input } from '@m/alchemy-ui/Input';
import { Form, Formik, type FormikErrors } from 'formik';
import { defineMessages, useIntl } from 'react-intl';
import { TranslatableInput } from '../challenge-management/components';
import { DEFAULT_LANGUAGE } from '../shared-constants';
import { type AppProps } from '../../common/interfaces';
import { Actions, CheckboxColumn, ContentRow, IntroText, TitleColumn, Wrapper } from './NewCustomPage.styled';

const PAGE_TRANSLATE_ID = 'new-page';

const messages = defineMessages({
  intro: {
    id: 'newCustomPageIntro',
    defaultMessage:
      'Custom pages can display elements with text, carousels, images and activity lists in any order you like.'
  },
  pageTitleLabel: {
    id: 'newCustomPagePageTitleLabel',
    defaultMessage: 'Page Title'
  },
  titlePlaceholder: {
    id: 'newCustomPageTitlePlaceholder',
    defaultMessage: "e.g. 'Home', 'F.A.Q.'"
  },
  requiredTitleError: {
    id: 'newCustomPageRequiredTitleError',
    defaultMessage: 'Provide a title in the default language to show this page in the navigation menu.'
  },
  showInNavigationLabel: {
    id: 'newCustomPageShowInNavigationLabel',
    defaultMessage: 'Show in navigation'
  },
  continueButton: {
    id: 'newCustomPageContinueButton',
    defaultMessage: 'Continue'
  }
});

interface NewPageFormValues {
  language: string;
  translations: Array<{
    id: string;
    communityId: string;
    iso: string;
    value: string;
    objectId: string;
    objectType: string;
    field: string;
  }>;
  page_navtitle: string;
  showInNavigation: boolean;
}

export const NewCustomPage = (_props: AppProps) => {
  const intl = useIntl();

  const titleLabel = intl.formatMessage(messages.pageTitleLabel);
  const requiredMessage = intl.formatMessage(messages.requiredTitleError);

  const initialValues: NewPageFormValues = {
    language: DEFAULT_LANGUAGE,
    translations: [],
    page_navtitle: '',
    showInNavigation: true
  };

  const validate = (values: NewPageFormValues) => {
    const errors: FormikErrors<NewPageFormValues> = {};
    if (!values.page_navtitle.trim()) {
      errors.page_navtitle = requiredMessage;
    }
    return errors;
  };

  return (
    <Wrapper data-testid="new-page-view">
      <IntroText>{intl.formatMessage(messages.intro)}</IntroText>

      <Formik<NewPageFormValues>
        initialValues={initialValues}
        validate={validate}
        onSubmit={(values) => {
          console.log('Form values on submit:', values);
          // TODO: call the dedicated core-api endpoint and redirect to edit page after success.
        }}
      >
        {({ errors, values, submitCount, handleBlur, handleChange, setFieldValue }) => {
          const titleError = submitCount > 0 && errors.page_navtitle;

          return (
            <Form>
              <ContentRow>
                <TitleColumn>
                  <Field
                    label={titleLabel}
                    required
                    isFullWidth
                    status={titleError ? { level: 'error', message: errors.page_navtitle } : undefined}
                    input={
                      <TranslatableInput
                        type="Page"
                        field="page_navtitle"
                        translateId={PAGE_TRANSLATE_ID}
                        fieldLabel={titleLabel}
                        id="page_navtitle"
                      >
                        <Input
                          id="page_navtitle"
                          name="page_navtitle"
                          value={values.page_navtitle}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={intl.formatMessage(messages.titlePlaceholder)}
                        />
                      </TranslatableInput>
                    }
                  />
                </TitleColumn>

                <CheckboxColumn>
                  <Checkbox
                    label={intl.formatMessage(messages.showInNavigationLabel)}
                    checked={values.showInNavigation}
                    onChange={(event) => setFieldValue('showInNavigation', event.target.checked)}
                  />
                </CheckboxColumn>
              </ContentRow>

              <Actions>
                <Button type="submit" priority="primary">
                  {intl.formatMessage(messages.continueButton)}
                </Button>
              </Actions>
            </Form>
          );
        }}
      </Formik>
    </Wrapper>
  );
};
